import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import BlueprintMark from '@/components/BlueprintMark';
import { toast } from 'sonner';
import {
  Reveal,
  StaggerGroup,
  StaggerItem,
  TextReveal,
  SpotlightCard,
  MagneticButton,
  TiltCard,
  Marquee,
} from '@/components/motion';

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/* A single spec callout: a corner-registration-mark frame around a small
   color sample (or icon), like a material/color reference on a technical
   drawing, rather than a flat filled UI swatch. */
const SpecSwatch = ({ color, label, icon }) => (
  <div>
    <div className="w-full aspect-square rounded-sm mb-2 relative">
      <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/40" />
      <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/40" />
      <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/40" />
      <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/40" />
      <div className="absolute inset-0 flex items-center justify-center">
        {icon ? (
          <Sparkles className="w-5 h-5 text-primary" />
        ) : (
          <div className="w-1/2 h-1/2 rounded-full" style={{ backgroundColor: color }} />
        )}
      </div>
    </div>
    <p className="font-mono text-[10px] text-muted-foreground">{label}</p>
  </div>
);

/* Brand spec-sheet mockup: a preview of an actual deliverable (type pairing +
   palette + mark), styled like a page from a studio's brand guidelines, rather
   than an abstract illustration. */
const BrandSpecSheet = () => (
  <div className="glass rounded-sm p-8 lg:p-10 max-w-md mx-auto w-full">
    <div className="mb-8">
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
        Brand Kit / Sample
      </span>
    </div>

    <div className="mb-6 pb-6 border-b border-border">
      <p className="font-heading text-6xl text-foreground leading-none mb-2">Aa</p>
      <p className="font-mono text-[11px] text-muted-foreground">Fraunces / Headings</p>
    </div>
    <div className="mb-8 pb-8 border-b border-border">
      <p className="font-body text-3xl text-foreground leading-none mb-2">Aa</p>
      <p className="font-mono text-[11px] text-muted-foreground">IBM Plex Sans / Body</p>
    </div>

    <div className="grid grid-cols-4 gap-4">
      <SpecSwatch color="#1B3A5C" label="#1B3A5C" />
      <SpecSwatch color="#C5A059" label="#C5A059" />
      <SpecSwatch color="#2D5F8A" label="#2D5F8A" />
      <SpecSwatch label="Mark" icon />
    </div>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { createProject, projects, selectProject } = useProject();
  const { isAuthenticated, user, login, loading: authLoading } = useAuth();
  const [showNewProject, setShowNewProject] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [industry, setIndustry] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    try {
      setLoading(true);
      const project = await createProject({
        name: projectName,
        industry: industry || undefined,
        description: ''
      });
      toast.success('Project created successfully!');
      setShowNewProject(false);
      navigate(`/dashboard/${project.id}`);
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueProject = async (projectId) => {
    try {
      await selectProject(projectId);
      navigate(`/dashboard/${projectId}`);
    } catch (error) {
      toast.error('Failed to load project');
    }
  };

  const pricingTiers = [
    {
      id: 'starter',
      tag: 'TIER-01',
      name: 'Starter',
      price: 39,
      description: 'Prove the brand before you build the rest.',
      features: [
        'Multi-project workspace',
        'Guided Setup Wizard',
        'Naming Studio',
        'Brand Kit',
        'Assets library',
      ],
      featured: false,
    },
    {
      id: 'pro',
      tag: 'TIER-02',
      name: 'Pro',
      price: 99,
      description: 'Full brand system plus a real digital presence.',
      features: [
        'Everything in Starter',
        'Strategy Module',
        'Logo Studio',
        'Website & App Shell',
        'Social Studio',
        'AI Chat assistant',
        'Timeline & client briefs',
      ],
      featured: true,
    },
    {
      id: 'agency',
      tag: 'TIER-03',
      name: 'Agency',
      price: 199,
      description: 'Run client delivery end to end, not just design it.',
      features: [
        'Everything in Pro',
        '106-checkpoint Project Status tracker',
        'Technical & Client Reports',
        'Higher project & seat caps',
      ],
      featured: false,
    },
  ];

  const pricingAddon = {
    label: 'Video Reels',
    price: '$49',
    note: '≈ 10 generations, pay-per-use — available on any tier',
  };

  const features = [
    {
      number: '01',
      title: "Brand Kit Creation",
      description: "Generate consistent color palettes, typography, and visual identity for your startup."
    },
    {
      number: '02',
      title: "AI Image Generation",
      description: "Create logos, banners, social media graphics, and packaging designs with AI."
    },
    {
      number: '03',
      title: "Smart Assistance",
      description: "Chat with AI to refine your designs and get expert recommendations."
    },
    {
      number: '04',
      title: "Production Ready",
      description: "Export assets in correct formats, DPI, and color spaces for any platform."
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Blueprint grid-paper texture */}
      <div className="absolute inset-0 blueprint-grid pointer-events-none" style={{ maskImage: 'linear-gradient(to bottom, black, transparent 70%)' }} />

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-border">
        <div className="flex items-center gap-2">
          <BlueprintMark className="w-7 h-7 text-primary" />
          <span className="font-heading font-semibold text-xl text-foreground tracking-tight">OMNICREATE</span>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={login}
            disabled={authLoading}
            variant="outline"
            className="rounded-sm px-5 py-2 bg-transparent border-black/15 text-foreground hover:bg-black/[0.03] font-body font-medium"
            data-testid="sign-in-google-btn"
          >
            <GoogleIcon className="w-4 h-4 mr-2" />
            Sign in with Google
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 px-6 md:px-12 pt-16 md:pt-24 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-28">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-8">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
                  Studio / Brand Systems
                </span>
              </div>
              <h1 className="font-heading font-normal text-5xl sm:text-6xl text-foreground mb-6 tracking-tight leading-[1.05]">
                <TextReveal as="span" text="Create your startup's" className="block" />
                <TextReveal
                  as="span"
                  text="complete visual identity"
                  className="block gradient-text italic"
                  delay={0.25}
                />
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg mb-10 leading-relaxed">
                From logos to websites, print materials to social media ads.
                OMNICREATE helps you build a cohesive brand in minutes, not months.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <MagneticButton>
                  <Button
                    onClick={login}
                    disabled={authLoading}
                    className="rounded-sm px-7 py-5 bg-primary text-primary-foreground font-body font-semibold hover:bg-primary/90 transition-colors"
                    data-testid="start-creating-btn"
                  >
                    Start Creating
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </MagneticButton>
                <Button
                  variant="outline"
                  className="rounded-sm px-7 py-5 bg-transparent text-foreground border-black/15 hover:bg-black/[0.03] transition-colors font-body"
                  data-testid="see-examples-btn"
                >
                  See Examples
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <TiltCard maxTilt={4}>
                <BrandSpecSheet />
              </TiltCard>
            </motion.div>
          </div>

          {/* Capability ticker: designer's tape-measure strip, not a logo wall */}
          <Reveal className="mb-28 border-y border-border">
            <Marquee speed={32}>
              {['Logos', 'Websites', 'Social Ads', 'Print & Packaging', 'Brand Strategy', 'Naming', 'Video Reels'].map(
                (item) => (
                  <span key={item} className="inline-flex items-center gap-2">
                    <span className="crosshair scale-50 opacity-60" />
                    {item}
                  </span>
                )
              )}
            </Marquee>
          </Reveal>

          {/* Features: numbered technical list, not icon cards */}
          <StaggerGroup className="border-t border-border mb-28" stagger={0.08}>
            {features.map((feature, index) => (
              <StaggerItem key={feature.title}>
                <SpotlightCard
                  as="div"
                  className="feature-card grid grid-cols-1 md:grid-cols-[80px_1fr_2fr] gap-4 md:gap-8 items-baseline py-8 border-b border-border px-2 -mx-2 rounded-sm"
                  data-testid={`feature-card-${index}`}
                >
                  <span className="font-mono text-sm text-primary">{feature.number}</span>
                  <h3 className="font-heading text-2xl text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
                    {feature.description}
                  </p>
                </SpotlightCard>
              </StaggerItem>
            ))}
          </StaggerGroup>

          {/* Pricing: rate card, styled as an addendum spec sheet rather than a marketing paywall */}
          <Reveal className="mb-28">
            <div className="mb-4">
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
                Studio / Rate Card
              </span>
            </div>
            <h2 className="font-heading text-4xl text-foreground mb-3 max-w-xl">
              Three ways to build
            </h2>
            <p className="text-muted-foreground max-w-lg mb-12 leading-relaxed">
              Every tier runs on the same AI generation underneath — you're paying for how much of the studio you get to run, not for tokens.
            </p>

            <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-6" stagger={0.08}>
              {pricingTiers.map((tier) => (
                <StaggerItem key={tier.id}>
                  <SpotlightCard
                    as="div"
                    className={`relative h-full flex flex-col glass rounded-sm p-8 ${
                      tier.featured ? 'ring-1 ring-accent/60' : ''
                    }`}
                    data-testid={`pricing-tier-${tier.id}`}
                  >
                    {tier.featured && (
                      <>
                        <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-accent" />
                        <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-accent" />
                        <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-accent" />
                        <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-accent" />
                      </>
                    )}

                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                        {tier.tag}
                      </span>
                      {tier.featured && (
                        <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-accent">
                          Recommended
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-2xl text-foreground mb-1">{tier.name}</h3>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{tier.description}</p>

                    <div className="mb-6 pb-6 border-b border-border flex items-baseline gap-1.5">
                      <span className="font-heading text-4xl text-foreground">${tier.price}</span>
                      <span className="font-mono text-xs text-muted-foreground">/ mo</span>
                    </div>

                    <ul className="space-y-2.5 mb-8 flex-1">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/90">
                          <span className="crosshair scale-50 opacity-60 mt-0.5 -ml-1 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    {tier.featured ? (
                      <MagneticButton>
                        <Button
                          onClick={login}
                          disabled={authLoading}
                          className="w-full rounded-sm px-7 py-5 bg-primary text-primary-foreground font-body font-semibold hover:bg-primary/90 transition-colors"
                          data-testid={`pricing-cta-${tier.id}`}
                        >
                          Start Creating
                        </Button>
                      </MagneticButton>
                    ) : (
                      <Button
                        onClick={login}
                        disabled={authLoading}
                        variant="outline"
                        className="w-full rounded-sm px-7 py-5 bg-transparent text-foreground border-black/15 hover:bg-black/[0.03] transition-colors font-body"
                        data-testid={`pricing-cta-${tier.id}`}
                      >
                        Start Creating
                      </Button>
                    )}
                  </SpotlightCard>
                </StaggerItem>
              ))}
            </StaggerGroup>

            {/* Add-on: itemized like an addendum on the same spec sheet, not a separate section */}
            <div className="mt-6 border-t border-dashed border-border pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  A-la-carte add-on
                </span>
                <span className="font-heading text-lg text-foreground">{pricingAddon.label}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">{pricingAddon.price}</span> {pricingAddon.note}
              </p>
            </div>

            <p className="mt-8 font-mono text-xs text-muted-foreground">
              Sign in to get started — plan selection at checkout is rolling out next.
            </p>
          </Reveal>

          {/* Recent Projects */}
          {projects.length > 0 && (
            <Reveal>
              <div className="mb-8">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
                  Continue Working
                </span>
              </div>
              <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" stagger={0.06}>
                {projects.slice(0, 3).map((project) => (
                  <StaggerItem key={project.id}>
                    <SpotlightCard
                      as="button"
                      onClick={() => handleContinueProject(project.id)}
                      className="glass rounded-sm p-5 text-left hover:border-primary/50 transition-all group w-full"
                      data-testid={`project-card-${project.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-heading text-lg text-foreground group-hover:text-primary transition-colors">
                          {project.name}
                        </h3>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      {project.industry && (
                        <span className="text-xs font-mono text-muted-foreground bg-black/[0.03] px-2 py-1 rounded-sm">
                          {project.industry}
                        </span>
                      )}
                    </SpotlightCard>
                  </StaggerItem>
                ))}
              </StaggerGroup>
            </Reveal>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border px-6 md:px-12 py-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <BlueprintMark className="w-5 h-5" />
            <span className="font-heading text-sm">OMNICREATE</span>
          </div>
          <p className="text-sm text-muted-foreground">
            AI-powered design for the modern startup
          </p>
          <p className="text-sm text-muted-foreground inline-flex flex-wrap items-center justify-center gap-1.5">
            Powered by{' '}
            <a
              href="https://blueprints.net.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors underline underline-offset-2"
            >
              Blueprints
            </a>
            <BlueprintMark className="w-4 h-4" />
            &middot; &copy; {new Date().getFullYear()}. All rights reserved.
          </p>
        </div>
      </footer>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="glass border-border text-foreground max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-foreground">Create New Project</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Start your brand journey. What are you building?
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-6 pt-4">
            <div className="space-y-2">
              <Label htmlFor="projectName" className="text-muted-foreground">Project Name</Label>
              <Input
                id="projectName"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., Green Bean Coffee"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/60 focus:ring-primary rounded-sm"
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry" className="text-muted-foreground">Industry (Optional)</Label>
              <Input
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., Coffee Shop, Tech Startup"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/60 focus:ring-primary rounded-sm"
                data-testid="industry-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewProject(false)}
                className="flex-1 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06] rounded-sm"
                data-testid="cancel-create-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 rounded-sm"
                data-testid="create-project-btn"
              >
                {loading ? (
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                ) : (
                  <>
                    Create
                    <Zap className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
